import { DatePipe } from '@angular/common'
import { Injectable } from '@angular/core'
import { DeliveryMode } from '@spartacus/cart/base/root'
import { Address, Principal, TranslationService } from '@spartacus/core'
import { Card } from '@spartacus/storefront'
import { combineLatest, Observable, of } from 'rxjs'
import { filter, map, take } from 'rxjs/operators'
import { EbActiveCartService } from '../../cart/services/eb-active-cart.service'
import { EbLanguageService } from '../../site-context/services/eb-language.service'
import { EbAddress, PartnerObject } from '../interface/eb-checkout.interface'

@Injectable()
export class EbCardConstructorService {
  constructor(
    public translation: TranslationService,
    protected activeCartService: EbActiveCartService,
    protected languageService: EbLanguageService
  ) {}

  getShippingAddressCard(deliveryAddress: Address | PartnerObject[]): Observable<Card> {
    return this.translation.translate('addressCard.shipTo').pipe(
      take(1),
      map(textTitle => {
        return !(deliveryAddress as any)?.length
          ? this.getCardAddress(textTitle, deliveryAddress as Address)
          : this.getCardAddress(
              textTitle,
              (deliveryAddress as PartnerObject[]).find(el => el.function === 'WE').address
            )
      })
    )
  }

  getOrderCodeCardContent(orderCode: string): Observable<Card> {
    return this.translation.translate('checkoutProgress.orderNo').pipe(
      filter(() => Boolean(orderCode)),
      map(textTitle => ({
        title: textTitle,
        text: [orderCode]
      }))
    )
  }

  getOrderCurrentDateCardContent(isoDate?: string): Observable<Card> {
    return combineLatest([
      this.languageService.getActive(),
      this.translation.translate('checkoutProgress.placedOn')
    ]).pipe(
      map(([lang, textTitle]) => {
        const date = new DatePipe(lang).transform(isoDate)
        return {
          title: textTitle,
          text: [date]
        }
      })
    )
  }

  getOrderShippingDateCardContent(isoDate?: string): Observable<Card> {
    return combineLatest([
      this.languageService.getActive(),
      this.translation.translate('checkoutProgress.shippingDate')
    ]).pipe(
      map(([lang, textTitle]) => {
        let date: string

        if (Boolean(isoDate)) {
          date = new DatePipe(lang).transform(isoDate)
        } else {
          date = new DatePipe(lang).transform(new Date())
        }

        return {
          title: textTitle,
          text: [date]
        }
      })
    )
  }

  getOrderStatusCardContent(status: string, statusCode?: string): Observable<Card> {
    return combineLatest([
      this.translation.translate('checkoutProgress.status'),
      status ? of(status) : this.translation.translate('statusText.' + statusCode)
    ]).pipe(
      map(([textTitle, statusText]) => ({
        title: textTitle,
        text: [statusText]
      }))
    )
  }

  getInvoiceAddressCard(invoiceAddress: Address): Observable<Card> {
    return this.translation.translate('addressCard.invoiceAddress').pipe(
      take(1),
      map(textTitle =>
        !(invoiceAddress as any)?.length
          ? this.getCardAddress(textTitle, invoiceAddress)
          : this.getCardAddress(
              textTitle,
              (invoiceAddress as PartnerObject[]).find(el => el.function === 'RE').address
            )
      )
    )
  }

  getCardAddress(textTitle: string, cardAddress: EbAddress): Card {
    let cardText: string[] = [
      cardAddress?.companyName || '',
      cardAddress?.name3 || '',
      (cardAddress?.line1 ?? '') + ' ' + (cardAddress?.line2 ?? ''),
      `${cardAddress?.postalCode || ''} ${cardAddress?.town || ''}`,
      cardAddress?.country?.name || ''
    ]
    cardText = cardText.filter(line => line !== '')
    return {
      title: textTitle,
      text: cardText
    } as Card
  }

  getPoNumberCard(poNumber: string): Observable<Card> {
    return combineLatest([
      this.translation.translate('checkoutReview.poNumber'),
      this.translation.translate('checkoutPO.noPoNumber')
    ]).pipe(
      map(([textTitle, noneTextTitle]) => {
        return {
          title: textTitle,
          textBold: poNumber ? poNumber : noneTextTitle
        }
      })
    )
  }

  getPaymentTypeCard(): Observable<Card> {
    return combineLatest([
      this.translation.translate('checkoutProgress.paymentDetails'),
      this.activeCartService.getActive()
    ]).pipe(
      map(([textTitle, cart]) => {
        return {
          title: textTitle,
          textBold: cart?.paymentType?.displayName
        }
      })
    )
  }

  getDeliveryCard(
    deliveryMode: DeliveryMode,
    shippingDate: string,
    completeDelivery = true,
    loading?: boolean
  ): Observable<Card> {
    return combineLatest([
      this.translation.translate('checkoutProgress.deliveryMode'),
      this.translation.translate('checkoutProgress.requestedShippingDate'),
      this.translation.translate(
        completeDelivery
          ? 'deliveryMode.selectMethod.complete'
          : 'deliveryMode.selectMethod.Partial'
      )
    ]).pipe(
      map(([textTitle, shippingDateTitle, deliveryModeType]) => {
        return {
          title: textTitle,
          textBold: deliveryMode.name,
          text: [
            deliveryMode?.description || deliveryModeType,
            deliveryMode?.description ? deliveryModeType : null,
            completeDelivery ? shippingDateTitle : null,
            completeDelivery ? shippingDate : null
          ],
          loading,
          positionToShowSpinner: 4
        } as Card
      })
    )
  }

  getYourContactsCard(user: Principal): Observable<Card> {
    return this.translation.translate('checkoutProgress.youContacts').pipe(
      map(textTitle => {
        return {
          title: textTitle,
          text: [user?.name]
        } as Card
      })
    )
  }

  getOrderedByCard(name: string): Observable<Card> {
    return this.translation.translate('checkoutProgress.orderedBy').pipe(
      map(textTitle => {
        return {
          title: textTitle,
          text: [name]
        } as Card
      })
    )
  }
}
